import { http } from '../lib/api/http';
import { uploadFileWithPresign } from './uploadsApi';
import { createNotification } from './notificationsApi';
import { mapApiUserToClient } from './userMapper';
import { setSessionUser } from './sessionUser';
const CURRENT_USER_ENDPOINT = '/users/me';
const PASSWORD_ENDPOINT = '/users/me/password';
const PUBLIC_USERS_ENDPOINT = '/public/users';
const USER_PROFILE_VIEWS_ENDPOINT = '/user-profile-views';
const AVATAR_UPLOAD_KIND = 'avatar';
const HERO_UPLOAD_KIND = 'hero';

const normalizeIdentifier = value => String(value ?? '').trim().replace(/^@/, '');

const mapPublicProfileResponse = data => {
	if (!data || typeof data !== 'object') {
		return {
			id: '',
			username: '',
			name: '',
			email: '',
			phoneNumber: '',
			tagline: '',
			aboutMe: '',
			avatarUrl: '',
			heroUrl: '',
			baseOperacion: '',
			followers: null,
			companySlug: '',
			companyName: '',
			companyId: '',
			companyMembership: '',
			role: '',
			updated: '',
			views: 0,
			allowVisitorDetails: false,
		};
	}

	const heroUrl =
		data.hero_url || data.profile_hero_url || data.heroUrl || data.profileHeroUrl || '';

	return {
		id: data.id || '',
		username: data.username || '',
		name: data.display_name || data.displayName || data.username || data.email || '',
		email: data.email || '',
		phoneNumber: data.phone_number || data.phoneNumber || '',
		tagline: data.tagline || '',
		aboutMe: data.about_me || data.aboutMe || '',
		avatarUrl: data.avatar_url || data.avatarUrl || '',
		heroUrl,
		baseOperacion:
			data.base_operacion ||
			data.baseOperacion ||
			data.company_location ||
			data.companyLocation ||
			'',
		followers:
			typeof data.followers === 'number' && Number.isFinite(data.followers)
				? Math.max(0, Math.round(data.followers))
				: null,
		companySlug: data.company_slug || data.companySlug || '',
		companyName: data.company_name || data.companyName || '',
		companyId: data.company_id || data.companyId || '',
		companyMembership:
			data.company_membership || data.companyMembership || data.membership_plan || '',
		role: data.role || '',
		updated: data.updated_at || data.updated || '',
		views: Number.isFinite(Number(data.views)) ? Math.max(0, Number(data.views)) : 0,
		allowVisitorDetails: Boolean(data.allow_visitor_details ?? data.allowVisitorDetails),
	};
};

const mapUserProfileViewRecord = record => {
	if (!record || typeof record !== 'object') {
		return {
			id: '',
			profileId: '',
			viewerId: '',
			viewerLabel: '',
			viewerUsername: '',
			viewerEmail: '',
			isGuest: true,
			created: '',
		};
	}
	const viewerId = record.viewer_id || record.viewerId || '';
	const viewerLabel =
		record.viewer_label ||
		record.viewerLabel ||
		(viewerId ? 'Miembro de Flotte' : 'Invitado');

	return {
		id: record.id || '',
		profileId: record.profile_id || record.profileId || '',
		viewerId,
		viewerLabel,
		viewerUsername: record.viewer_username || record.viewerUsername || '',
		viewerEmail: record.viewer_email || record.viewerEmail || '',
		isGuest: !viewerId,
		created: record.created_at || record.created || record.updated_at || '',
	};
};

const patchCurrentUser = async payload => {
  const response = await http.patch(CURRENT_USER_ENDPOINT, payload);
  const mapped = mapApiUserToClient(response.data || {});
  setSessionUser(mapped);
  return mapped;
};

export async function fetchCurrentUserProfile() {
  const response = await http.get(CURRENT_USER_ENDPOINT);
  const mapped = mapApiUserToClient(response.data || {});
  setSessionUser(mapped);
  return mapped;
}

const normalizeId = value => {
  if (!value) return '';
  if (Array.isArray(value)) {
    return value[0] || '';
  }
  if (typeof value === 'object' && value !== null) {
    return value.id || '';
  }
  return String(value);
};

const buildViewerProfileUrl = (username, id) => {
  const slug = String(username || '').trim();
  if (slug) {
    return `/usuario/${encodeURIComponent(slug)}`;
  }
  const normalizedId = normalizeId(id);
  return normalizedId ? `/usuario/${encodeURIComponent(normalizedId)}` : '';
};


export async function updateUserPhone(phoneNumber) {
  const normalized = (phoneNumber || '').trim();
  return patchCurrentUser({ phone_number: normalized });
}

export async function updateUserAboutMe(aboutMe) {
  const normalized = (aboutMe ?? '').trim();
  return patchCurrentUser({ about_me: normalized });
}

export async function updateUserProfileBasics({ name, phoneNumber, baseOperacion } = {}) {
  const payload = {};

  if (name !== undefined) {
    payload.display_name = (name ?? '').trim();
  }

  if (phoneNumber !== undefined) {
    payload.phone_number = (phoneNumber ?? '').trim();
  }

  if (baseOperacion !== undefined) {
    payload.base_operacion = (baseOperacion ?? '').trim();
  }

  if (!Object.keys(payload).length) {
    return fetchCurrentUserProfile();
  }

  return patchCurrentUser(payload);
}

export async function updateUserAvatar(file) {
  if (!(file instanceof File)) {
    throw new Error('Selecciona una imagen válida.');
  }
  const presign = await uploadFileWithPresign({ kind: AVATAR_UPLOAD_KIND, file });
  return patchCurrentUser({ avatar_url: presign?.public_url || '' });
}

export function buildUserAvatarUrl(record) {
  if (!record) return '';
  return record.avatarUrl || record.avatar_url || record.avatar || '';
}

export function buildUserHeroUrl(record) {
  if (!record) return '';
  return (
    record.heroUrl ||
    record.profileHero ||
    record.profileHeroUrl ||
    record.profile_hero_url ||
    ''
  );
}

export async function updateUserHero(file) {
  if (!(file instanceof File)) {
    throw new Error('Selecciona una imagen válida.');
  }
  const presign = await uploadFileWithPresign({ kind: HERO_UPLOAD_KIND, file });
  return patchCurrentUser({ profile_hero_url: presign?.public_url || '' });
}

export async function removeUserHero() {
  return patchCurrentUser({ profile_hero_url: '' });
}

export async function updateUserPassword(newPassword) {
	const normalizedPassword = String(newPassword || '').trim();
	if (!normalizedPassword) {
		throw new Error('La nueva contraseña es obligatoria.');
	}
	await http.post(PASSWORD_ENDPOINT, { new_password: normalizedPassword });
	return true;
}

export async function fetchUserPublicProfile(identifier) {
	const normalized = normalizeIdentifier(identifier);
	if (!normalized) {
		throw new Error('No se proporcionó el identificador del perfil.');
	}
	const response = await http.get(`${PUBLIC_USERS_ENDPOINT}/${encodeURIComponent(normalized)}`);
	return mapPublicProfileResponse(response.data || {});
}

export async function registerUserProfileVisit(input) {
	const isObjectInput = input && typeof input === 'object';
	const identifierValue = isObjectInput ? input.identifier ?? input.userId ?? input.id : input;
	const normalized = normalizeIdentifier(identifierValue);
	if (!normalized) {
		return null;
	}

	const viewerId = ((isObjectInput ? input.viewerId : '') || '').trim();
	const viewerLabel = ((isObjectInput ? input.viewerLabel : '') || '').trim();
	const viewerUsername = ((isObjectInput ? input.viewerUsername : '') || '').trim();
	const profileData = (isObjectInput ? input.profile : null) || null;

	try {
		const response = await http.post(
			`${PUBLIC_USERS_ENDPOINT}/${encodeURIComponent(normalized)}/visits`,
			viewerLabel ? { viewer_label: viewerLabel } : {}
		);
		const views = Number(response.data?.views);
		const profileId = profileData?.id || '';
		const companyId = profileData?.companyId || '';
		const allowVisitorDetails = Boolean(profileData?.allowVisitorDetails);
		const viewerDisplay = viewerLabel || (viewerId ? 'Miembro de Flotte' : 'Invitado');

		if (companyId && profileId && viewerId && viewerId !== profileId) {
			const detailViewerId = allowVisitorDetails ? viewerId : '';
			const detailViewerLabel = allowVisitorDetails
				? viewerDisplay
				: viewerId
				? 'Usuario registrado'
				: 'Invitado';
			const detailViewerUsername = allowVisitorDetails ? viewerUsername : '';
			const detailViewerUrl = allowVisitorDetails
				? buildViewerProfileUrl(detailViewerUsername, detailViewerId)
				: '';
			const message = allowVisitorDetails
				? `${viewerDisplay} visitó tu perfil.`
				: 'Un usuario visitó tu perfil.';

			createNotification({
				type: 'profile_view',
				title: 'Nueva visita a tu perfil',
				message,
				companyId,
				destinationUrl: '/dashboard?section=estadisticas-reportes',
				createdById: viewerId || undefined,
				data: {
					targetUserId: profileId,
					viewerId: detailViewerId || null,
					viewerLabel: detailViewerLabel,
					viewerUsername: detailViewerUsername,
					viewerUrl: detailViewerUrl,
				},
			}).catch(error => {
				console.warn('No se pudo crear la notificación de visita de perfil.', error);
			});
		}

		return Number.isFinite(views) ? Math.max(0, views) : null;
	} catch (error) {
		console.warn('No se pudo registrar la visita del perfil de usuario.', error);
		return null;
	}
}

export async function listUserProfileViews(profileId, { limit = 20 } = {}) {
	if (!profileId) {
		return { items: [], total: 0 };
	}

	try {
		const response = await http.get(USER_PROFILE_VIEWS_ENDPOINT, {
			params: { profile_id: profileId },
		});
		const items = Array.isArray(response.data) ? response.data : [];
		return {
			items: items.slice(0, limit).map(mapUserProfileViewRecord),
			total: items.length,
		};
	} catch (error) {
		if (error?.status === 404) {
			return { items: [], total: 0 };
		}
		console.warn('No se pudieron obtener las visitas recientes del perfil de usuario.', error);
		return { items: [], total: 0 };
	}
}
